import type { Root, Code } from 'mdast';
import { NodeRenderer } from './NodeRenderer';
import { TreeBuilder } from './TreeBuilder';
import { CodeBlockNode } from './types';
import { visit } from 'unist-util-visit';
import { getDefaultColorDefinitions } from './platformColorDefaults';

/**
 * Remark plugin to transform custom code blocks into React components
 */
function plugin(options?: { platform?: string }) {
  return (tree: Root) => {
    const defaultColors = getDefaultColorDefinitions(options?.platform);

    visit(tree, 'code', (node: Code) => {
      if (!node.value.trim().startsWith('@code')) {
        return;
      }

      const lines = node.value.split('\n');
      const builder = new TreeBuilder(lines, defaultColors);
      const parsedTree = builder.build();
      const colorDefinitions = builder.getColorDefinitions();

      if (!node.data) {
        node.data = {};
      }
      node.data.hProperties = {
        ...node.data.hProperties,
        'data-codey-blocks': JSON.stringify(parsedTree),
        'data-color-definitions': JSON.stringify(Object.fromEntries(colorDefinitions)),
      };
    });
  };
}

/**
 * Custom components for react-markdown
 */
const components = {
  code({ inline, className, children, ...props }: ComponentsProps) {
    const codeyBlocksData = props['data-codey-blocks'];
    const colorDefinitionsData = props['data-color-definitions'];

    if (codeyBlocksData && !inline) {
      try {
        const tree = JSON.parse(codeyBlocksData) as CodeBlockNode[];
        const colorDefinitions = colorDefinitionsData ? new Map(Object.entries(JSON.parse(colorDefinitionsData))) : new Map();

        const renderer = new NodeRenderer();
        renderer.setColorDefinitions(colorDefinitions);

        return <div>{tree.map((node, index) => renderer.render(node, index))}</div>;
      } catch (e) {
        console.error('Failed to parse codey blocks:', e);
      }
    }

    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
};

type ComponentsProps = {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
  'data-codey-blocks'?: string;
  'data-color-definitions'?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export const RemarkCodeBlockPlugin = {
  plugin,
  components,
};
